import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-30586dff"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "lifespan-backdrop" }
const _hoisted_2 = { class: "age-line-label" }
const _hoisted_3 = { class: "ticks" }
const _hoisted_4 = ["isMajor"]
const _hoisted_5 = {
  key: 0,
  class: "major-tick-label"
}
const _hoisted_6 = {
  key: 0,
  class: "main-icon death-icon"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = {
  key: 1,
  class: "main-icon main-age"
}
const _hoisted_9 = { class: "main-icon birth-icon" }
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  key: 0,
  class: "self-band"
}
const _hoisted_12 = { class: "band-header" }
const _hoisted_13 = { class: "band-label self-label" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["src"]
const _hoisted_16 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "event-dot" }, null, -1))
const _hoisted_17 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "event-dot" }, null, -1))
const _hoisted_18 = {
  key: 2,
  class: "event-dot"
}
const _hoisted_19 = { class: "event-type-name" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "band-label" }
const _hoisted_22 = {
  key: 0,
  class: "band-count"
}
const _hoisted_23 = { class: "band-content" }
const _hoisted_24 = { class: "person-name-wrapper" }
const _hoisted_25 = { class: "person-name" }
const _hoisted_26 = {
  key: 0,
  class: "more-label"
}

import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { ArrowRightIcon, ChevronRightIcon } from '@zhuowenli/vue-feather-icons'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { ViewPerson, Assertion } from '@/rd/ResearchDataModel'
import { HistoricalDateType, HistoricalDateFormat } from '@/rd/HistoricalDateEnums'
import { SortDate } from '@/rd/SortDate'
import { DurationUtil } from '@/util/LuxonUtil'
import { isDefined } from '@/util/TypeScriptUtil'
import { AssertionTypeIcons } from '../AssertionTypeIcons'
import LifespanBar from './LifespanBar.vue'
import { DateTime } from 'luxon'
import { AssertionType } from '@/rd/ResearchDataModel'
import { uniq, flatten } from 'lodash'
import { Collapse } from 'bootstrap'

const timelineRange = 500 // years
const minorTickInterval = 10 // years
const majorTickInterval = 100 // years
const remPerYear = 0.2 // 4 rem / 10 years
interface BandDef {
  key: string
  label: string
  persons: ViewPerson[]
}

interface SelfEvent {
  id: string
  typeName: string
  year: number
  startYear?: number
  endYear?: number
  icon?: string
  isSpan: boolean
  dateText: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Timeline',
  props: {
  personId: String,
},
  setup(__props) {

const props = __props;



const timelineContainerRef = ref<HTMLElement | null>(null)
const timelineBandsRef = ref<HTMLElement | null>(null)
const viewWidthInYears = ref(0)
const resizeObserver = new ResizeObserver(() => {
  if (timelineContainerRef.value) {
    const viewWidthInRem = timelineContainerRef.value.clientWidth / 16 // 1rem = 16px
    viewWidthInYears.value = viewWidthInRem / remPerYear
  }
})

const currentYear = DateTime.now().year
const ticks: number[] = []
const firstTickYear = Math.floor(currentYear / minorTickInterval) * minorTickInterval;
for (let t = firstTickYear; t > currentYear - timelineRange; t -= minorTickInterval) {
  ticks.push(t)
}

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const assertionStore = useAssertionStore()

const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId)?.data)
const deathYearRef = computed(() => viewPersonRef.value?.displayProperties.deathSortDate?.date1.year)
const birthYearRef = computed(() => viewPersonRef.value?.displayProperties.birthSortDate?.date1.year)

const midYearRef = computed(() => {
  if (viewPersonRef.value?.displayProperties.living)
    return currentYear
  if (birthYearRef.value && deathYearRef.value)
    return (birthYearRef.value + deathYearRef.value) / 2
  if (birthYearRef.value)
    return birthYearRef.value + 40
  if (deathYearRef.value)
    return deathYearRef.value - 40
  return undefined
})
const maxViewYearRef = ref(currentYear)
watch(midYearRef, () => {
  if (midYearRef.value) {
    maxViewYearRef.value = Math.min(Math.floor(midYearRef.value + (viewWidthInYears.value / 2)), currentYear)
  }
})

// expanded state
const isExpanded = ref(false)
const expandedBands = reactive(new Set<string>())
const expandedEventId = ref<string | null>(null)

watch(() => props.personId, () => {
  expandedBands.clear()
  expandedEventId.value = null
})

// main timeline lifespan bar with b/d icons and age
const mainDeathYearRef = computed(() => deathYearRef.value ?? (viewPersonRef.value?.displayProperties.living ? currentYear : undefined))

const hasMainLifespan = computed(() => !!(birthYearRef.value && mainDeathYearRef.value))

const mainLifespanStyleRef = computed(() => {
  const birth = birthYearRef.value
  const death = mainDeathYearRef.value
  if (!birth || !death) return { left: '0rem', width: '0rem' }
  return {
    left: yearPosition(death),
    width: yearWidth(birth, death),
  }
})

const mainAgeRef = computed(() => {
  if (!viewPersonRef.value) return undefined
  return getAge(viewPersonRef.value)
})

// age ticks: vertical lines every 10 years of age
const ageTicksRef = computed(() => {
  if (!birthYearRef.value) return []
  const endYear = deathYearRef.value ?? (viewPersonRef.value?.displayProperties.living ? currentYear : (birthYearRef.value + 80))
  const result = []
  for (let age = 10; birthYearRef.value + age <= endYear; age += 10) {
    result.push({ year: birthYearRef.value + age, age })
  }
  return result
})

function yearPosition(year: number) {
  const yearOffset = currentYear - year
  return `${yearOffset * remPerYear}rem`
}

function yearWidth(startYear: number, endYear: number) {
  const years = endYear - startYear
  return `${years * remPerYear}rem`
}

function getEventIcon(event: { type: string }) {
  return AssertionTypeIcons.getIcon(event.type)
}

function onBandsShown() { isExpanded.value = true }
function onBandsHidden() { isExpanded.value = false }

onMounted(() => {
  if (timelineContainerRef.value) {
    resizeObserver.observe(timelineContainerRef.value)
  }
  if (timelineBandsRef.value) {
    timelineBandsRef.value.addEventListener('shown.bs.collapse', onBandsShown)
    timelineBandsRef.value.addEventListener('hidden.bs.collapse', onBandsHidden)
  }
})

onBeforeUnmount(() => {
  if (timelineBandsRef.value) {
    timelineBandsRef.value.removeEventListener('shown.bs.collapse', onBandsShown)
    timelineBandsRef.value.removeEventListener('hidden.bs.collapse', onBandsHidden)
  }
})

// --- Expanded bands ---

function toggleTimeline() {
  if (timelineBandsRef.value) {
    new Collapse(timelineBandsRef.value)
  }
}

function setBandExpanded(key: string, expanded: boolean) {
  if (expanded) expandedBands.add(key)
  else expandedBands.delete(key)
}

function bandMoreCount(band: BandDef) {
  return Math.max(0, band.persons.filter(p => hasBarPosition(p)).length - 10)
}

// Family member computeds

const parentsRef = computed(() => {
  if (!props.personId) return []
  const { parentIds } = viewPersonStore.getLoadedParentIds(props.personId)
  return viewPersonStore.getLoadedPersonList(parentIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const grandparentsRef = computed(() => {
  if (!props.personId) return []
  const { parentIds } = viewPersonStore.getLoadedParentIds(props.personId)
  const gpIds = uniq(flatten(parentIds.map(pid => viewPersonStore.getLoadedParentIds(pid).parentIds)))
  return viewPersonStore.getLoadedPersonList(gpIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const siblingsRef = computed(() => {
  if (!props.personId) return []
  const { parentFamilies } = viewPersonStore.getLoadedParentFamilies(props.personId)
  const siblingIds = uniq(flatten(
    parentFamilies.map(pf => viewFamilyStore.asyncChildKeys.get(pf.id!)?.data?.keys ?? [])
  )).filter(id => id !== props.personId)
  return viewPersonStore.getLoadedPersonList(siblingIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const spouseFamiliesRef = computed(() => {
  if (!props.personId) return []
  return viewPersonStore.getLoadedSpouseFamilies(props.personId).spouseFamilies
})

const spousesRef = computed(() => {
  if (!props.personId) return []
  const spouseIds = spouseFamiliesRef.value
    .map(sf => sf.spouseOf(props.personId!))
    .filter(isDefined)
  return viewPersonStore.getLoadedPersonList(spouseIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const childrenRef = computed(() => {
  const children = flatten(
    spouseFamiliesRef.value.map(sf => viewFamilyStore.getLoadedChildren(sf.id!).children)
  )
  return children
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const grandchildrenRef = computed(() => {
  const gcList = flatten(
    childrenRef.value.map(child => {
      const childSpouseFamilies = viewPersonStore.getLoadedSpouseFamilies(child.id!).spouseFamilies
      return flatten(childSpouseFamilies.map(csf => viewFamilyStore.getLoadedChildren(csf.id!).children))
    })
  )
  return gcList
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const bandsRef = computed((): BandDef[] => [
  { key: 'grandparents', label: 'Grandparents', persons: grandparentsRef.value },
  { key: 'parents',      label: 'Parents',      persons: parentsRef.value },
  { key: 'siblings',     label: 'Siblings',     persons: siblingsRef.value },
  { key: 'self',         label: 'Self',         persons: [] },
  { key: 'spouses',      label: 'Spouses',      persons: spousesRef.value },
  { key: 'children',     label: 'Children',     persons: childrenRef.value },
  { key: 'grandchildren', label: 'Grandchildren', persons: grandchildrenRef.value },
])

// Self events

const selfEventsRef = computed((): SelfEvent[] => {
  if (!props.personId) return []
  const personAssertions = assertionStore.getLoadedForPerson(props.personId)
  const familyAssertions = spouseFamiliesRef.value.flatMap(sf => assertionStore.getLoadedForFamily(sf.id!))
  return [...personAssertions, ...familyAssertions]
    .filter(a => a.sortDate?.date1?.year != null)
    .sort(Assertion.compareDate)
    .map(a => {
      const isSpan = a.date.type === HistoricalDateType.Span
      return {
        id: a.id!,
        typeName: a.assertionType,
        year: a.sortDate.date1!.year!,
        startYear: isSpan ? a.date.date1?.year : undefined,
        endYear: isSpan ? a.date.date2?.year : undefined,
        icon: AssertionTypeIcons.hasIcon(a.assertionType) ? AssertionTypeIcons.getIcon(a.assertionType) : undefined,
        isSpan,
        dateText: a.date.toString(HistoricalDateFormat.LongDisplay),
      }
    })
})

// Bar positioning helpers

function hasBarPosition(person: ViewPerson) {
  return person.birthYear != null || person.deathYear != null
}

function getBarPositionStyle(person: ViewPerson | undefined) {
  if (!person) return {}
  const birthYear = person.birthYear ?? (person.deathYear ? person.deathYear - 80 : undefined)
  const deathYear = person.deathYear ?? (person.displayProperties.living ? currentYear : (person.birthYear ? person.birthYear + 80 : undefined))
  if (birthYear == null || deathYear == null) return {}
  // timeline runs right-to-left: present (small left) → past (large left)
  // bar starts at death year (closer to present) and extends toward birth year
  return { left: yearPosition(deathYear), width: yearWidth(birthYear, deathYear) }
}

function getEventPositionStyle(evt: SelfEvent) {
  if (evt.isSpan && evt.endYear != null) {
    // position at end year (more recent = further left on timeline)
    return { left: yearPosition(evt.endYear) }
  }
  return { left: yearPosition(evt.year) }
}

function getAge(p: ViewPerson) {
  const dp = p.displayProperties
  const birth = dp.birthSortDate.date1
  const death = dp.deathSortDate.date1
  if (birth && !birth.isEmpty && death && !death.isEmpty) {
    return DurationUtil.toAge(death.diff(birth), true)
  }
  return undefined
}


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "timelineContainerRef",
    ref: timelineContainerRef,
    class: _normalizeClass(["timeline-container", { expanded: isExpanded.value }]),
    onClick: toggleTimeline
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        class: "band-timeline",
        style: _normalizeStyle({ left: yearWidth(_unref(currentYear), maxViewYearRef.value) })
      }, [
        (viewPersonRef.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "backdrop-bar",
              style: _normalizeStyle(getBarPositionStyle(viewPersonRef.value))
            }, null, 4))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ageTicksRef.value, (at) => {
          return (_openBlock(), _createElementBlock("div", {
            key: at.age,
            class: "age-line",
            style: _normalizeStyle({ left: yearPosition(at.year) })
          }, [
            _createElementVNode("span", _hoisted_2, _toDisplayString(at.age), 1)
          ], 4))
        }), 128))
      ], 4)
    ]),
    _createElementVNode("div", {
      class: "timeline",
      style: _normalizeStyle({ left: yearWidth(_unref(currentYear), maxViewYearRef.value) })
    }, [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ticks, (t) => {
          return _createElementVNode("div", {
            key: t,
            class: "tick",
            isMajor: t % majorTickInterval == 0,
            style: _normalizeStyle({ left: yearPosition(t) })
          }, [
            (t % majorTickInterval == 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(t), 1))
              : _createCommentVNode("", true)
          ], 12, _hoisted_4)
        }), 64))
      ]),
      _createElementVNode("div", {
        class: "main-lifespan",
        style: _normalizeStyle(mainLifespanStyleRef.value)
      }, [
        (hasMainLifespan.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (deathYearRef.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createElementVNode("img", {
                      src: getEventIcon({ type: _unref(AssertionType).Death })
                    }, null, 8, _hoisted_7)
                  ]))
                : _createCommentVNode("", true),
              (mainAgeRef.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(mainAgeRef.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("img", {
                  src: getEventIcon({ type: _unref(AssertionType).Birth })
                }, null, 8, _hoisted_10)
              ])
            ], 64))
          : _createCommentVNode("", true)
      ], 4)
    ], 4),
    _createElementVNode("div", {
      ref_key: "timelineBandsRef",
      ref: timelineBandsRef,
      class: "timeline-bands collapse",
      onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bandsRef.value, (band) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: band.key
        }, [
          (band.persons.length > 0 || band.key === 'self')
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["category-band", { expanded: expandedBands.has(band.key) }])
              }, [
                (band.key === 'self')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("span", _hoisted_13, _toDisplayString(band.label), 1)
                      ]),
                      _createElementVNode("div", {
                        class: "band-timeline",
                        style: _normalizeStyle({ left: yearWidth(_unref(currentYear), maxViewYearRef.value) })
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selfEventsRef.value, (evt) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: evt.id,
                            class: _normalizeClass(["event-row", { expanded: expandedEventId.value === evt.id }]),
                            onClick: ($event: any) => (expandedEventId.value = expandedEventId.value === evt.id ? null : evt.id)
                          }, [
                            _createElementVNode("div", {
                              class: "event-marker",
                              style: _normalizeStyle(getEventPositionStyle(evt))
                            }, [
                              (evt.icon)
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: evt.icon,
                                    class: _normalizeClass(["event-type-icon", evt.typeName])
                                  }, null, 10, _hoisted_15))
                                : _createCommentVNode("", true),
                              (evt.isSpan && evt.endYear)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _hoisted_16,
                                    _createElementVNode("div", {
                                      class: "span-bar",
                                      style: _normalizeStyle({ width: yearWidth(evt.endYear, evt.startYear!) })
                                    }, null, 4),
                                    _hoisted_17
                                  ], 64))
                                : (_openBlock(), _createElementBlock("span", _hoisted_18)),
                              _createElementVNode("span", _hoisted_19, _toDisplayString(evt.typeName), 1)
                            ], 4),
                            _createElementVNode("div", {
                              class: "event-detail",
                              style: _normalizeStyle(getEventPositionStyle(evt))
                            }, _toDisplayString(evt.dateText), 5)
                          ], 10, _hoisted_14))
                        }), 128))
                      ], 4)
                    ]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("div", {
                        class: "band-header expandable",
                        onClick: ($event: any) => (setBandExpanded(band.key, !expandedBands.has(band.key)))
                      }, [
                        _createElementVNode("button", {
                          type: "button",
                          class: _normalizeClass(["expand-chevron", { expanded: expandedBands.has(band.key) }])
                        }, [
                          _createVNode(_unref(ChevronRightIcon))
                        ], 2),
                        _createElementVNode("span", _hoisted_21, _toDisplayString(band.label), 1),
                        (band.persons.length > 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_22, "(" + _toDisplayString(band.persons.length) + ")", 1))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_20),
                      _createElementVNode("div", _hoisted_23, [
                        _createElementVNode("div", {
                          class: "band-timeline",
                          style: _normalizeStyle({ left: yearWidth(_unref(currentYear), maxViewYearRef.value) })
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(band.persons, (p, i) => {
                            return _withDirectives((_openBlock(), _createElementBlock("div", {
                              key: p.id,
                              class: _normalizeClass(["person-bar-row", { expanded: expandedBands.has(band.key) }])
                            }, [
                              _createElementVNode("div", {
                                class: "person-bar-inner",
                                style: _normalizeStyle(getBarPositionStyle(p))
                              }, [
                                _createElementVNode("div", _hoisted_24, [
                                  _createElementVNode("div", _hoisted_25, [
                                    _createTextVNode(_toDisplayString(p.displayName) + " ", 1),
                                    _createVNode(_component_router_link, {
                                      to: p.pageUrl!,
                                      class: "person-link",
                                      "aria-label": "View person",
                                      onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(ArrowRightIcon))
                                      ]),
                                      _: 2
                                    }, 1032, ["to"])
                                  ])
                                ]),
                                _createVNode(LifespanBar, {
                                  "birth-year": p.birthYear,
                                  "death-year": p.deathYear,
                                  age: getAge(p),
                                  living: p.displayProperties.living,
                                  layout: expandedBands.has(band.key) ? 'full' : 'tiny'
                                }, null, 8, ["birth-year", "death-year", "age", "living", "layout"])
                              ], 4)
                            ], 2)), [
                              [_vShow, hasBarPosition(p) && (expandedBands.has(band.key) || i < 10)]
                            ])
                          }), 128))
                        ], 4),
                        (!expandedBands.has(band.key) && bandMoreCount(band) > 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_26, "(" + _toDisplayString(bandMoreCount(band)) + " more)", 1))
                          : _createCommentVNode("", true)
                      ])
                    ], 64))
              ], 2))
            : _createCommentVNode("", true)
        ], 64))
      }), 128))
    ], 512)
  ], 2))
}
}

})